/**
 * Oversight — everyone you supervise, today, filtered by the number you tapped.
 * Route: /oversight
 * @file src/web/features/oversight/pages/index.tsx
 *
 * @llm-rule WHEN: A Head or the Company Admin wants to see who needs attention today
 * @llm-rule NOTE: ONE SOURCE OF TRUTH. Every number on this screen is counted
 *   from the same `people` array that the list below renders, so a tile can
 *   never disagree with the rows it filters to. The screen used to print three
 *   independent count rows — five badges from /summary, four tiles computed in
 *   the browser, and two more in a tab switch — where "overdue" and the people
 *   totals each appeared twice from different queries.
 * @llm-rule NOTE: Every tile counts PEOPLE, because the list under it is
 *   people. Flag totals are sentences in the subtitle, not tiles, so a number
 *   never means "flags" in one tile and "people" in the next.
 * @llm-rule AVOID: Declaring sub-components inside the page function — they
 *   remount on every render and the screen loses its scroll position.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button, Skeleton, toast } from '@bloomneo/uikit';
import { ChevronRight, Search, X } from 'lucide-react';
import { AuthGuard, useAuth } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { PersonAvatar } from '../../../shared/components/presence';
import { FlagCount } from '../components/OversightChrome';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import { greeting, isSupervisor, ROLE_LABEL, type OrgRole } from '../../../shared/domain';
import { FLAG_LABEL, type FlagCode } from '../../../shared/flags';

interface PersonRow {
  id: string;
  name: string | null;
  email: string;
  orgRole: OrgRole;
  title: string | null;
  initials: string | null;
  team: { id: string; name: string } | null;
  department: { id: string; name: string } | null;
  flags: { open: number; total: number; byCode: Array<{ code: FlagCode | null; count: number }> };
  tasks: { pending: number; overdue: number };
  today: { present: boolean; offSite: boolean; onLeave: boolean; flagsToday: number };
}

/** The buckets a tile can filter to. `all` is the unfiltered list. */
type Bucket = 'all' | 'in' | 'leave' | 'out' | 'flagged' | 'overdue';

const BUCKET_EMPTY: Record<Bucket, string> = {
  all: 'Nobody is in your scope yet.',
  in: 'Nobody has clocked in yet today.',
  leave: 'Nobody is on approved leave today.',
  out: 'Everyone is either in or on leave.',
  flagged: 'Nobody is carrying an open flag.',
  overdue: 'No overdue tasks. ',
};

/** Does this person belong in that bucket? The one place the rule is written. */
function inBucket(person: PersonRow, bucket: Bucket): boolean {
  switch (bucket) {
    case 'in':
      return person.today.present;
    case 'leave':
      return person.today.onLeave;
    // Approved leave is NOT "yet to clock in" — they are not expected today.
    case 'out':
      return !person.today.present && !person.today.onLeave;
    case 'flagged':
      return person.flags.open > 0;
    case 'overdue':
      return person.tasks.overdue > 0;
    default:
      return true;
  }
}

/**
 * A count you can act on.
 *
 * Every tile is a button: the number tells you something is there, and the tap
 * shows you who. A count that cannot be opened just moves the work of finding
 * those people back onto the reader.
 */
function FilterTile({
  n,
  label,
  active,
  tone = 'plain',
  onClick,
}: {
  n: number;
  label: string;
  active: boolean;
  tone?: 'plain' | 'alert' | 'good' | 'warn';
  onClick: () => void;
}) {
  const accent: Record<string, string> = {
    plain: 'var(--ink)',
    alert: 'var(--dot-flag)',
    good: 'var(--dot-onsite)',
    warn: 'var(--dot-offsite)',
  };
  // A zero is muted rather than hidden: a row whose tiles come and go has to be
  // re-read every time.
  const colour = n > 0 ? accent[tone] : 'var(--ink-ghost)';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="flex min-w-[92px] flex-1 flex-col items-start rounded-[12px] px-3 py-2.5 text-left transition-all active:scale-[0.98]"
      style={{
        border: active ? '2px solid var(--ink)' : '1.5px solid var(--rule)',
        background: active ? 'var(--paper-sunk)' : 'var(--paper)',
      }}
    >
      <span className="font-clock text-[20px] leading-none tabular-nums" style={{ color: colour }}>
        {n}
      </span>
      <span className="mt-1 text-[11px] leading-tight text-ink-faint">{label}</span>
    </button>
  );
}

function PersonLine({ person }: { person: PersonRow }) {
  const { today } = person;

  // What is true of them RIGHT NOW, in the order it matters.
  const state = today.onLeave
    ? { text: 'on approved leave', colour: 'var(--dot-offsite)' }
    : today.present
      ? {
          text: today.offSite ? 'clocked in · off-site' : 'clocked in',
          colour: 'var(--dot-onsite)',
        }
      : { text: 'not clocked in', colour: 'var(--ink-faint)' };

  return (
    <li style={{ borderTop: '1.5px dashed var(--rule)' }}>
      <Link
        to={`/oversight/${person.id}`}
        className="flex items-center gap-3 py-3 transition-colors hover:bg-paper-warm"
      >
        <PersonAvatar person={person} size={34} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-[13.5px] font-medium text-ink">{person.name}</span>
            <span className="text-[11.5px] text-ink-faint">
              {person.title || ROLE_LABEL[person.orgRole]}
            </span>
            {person.team && (
              <span className="text-[11px] text-ink-ghost">· {person.team.name}</span>
            )}
          </div>

          <div className="mt-0.5 text-[11.5px]" style={{ color: state.colour }}>
            {state.text}
          </div>

          {/* The breakdown, so a count is never just a number. */}
          <div className="mt-1 flex flex-wrap gap-1.5">
            {person.flags.byCode.map((entry) => (
              <span
                key={entry.code ?? 'other'}
                className="rounded-full bg-paper-sunk px-2 py-0.5 text-[10.5px] text-ink-faint"
              >
                {entry.code ? FLAG_LABEL[entry.code] : 'Other'} · {entry.count}
              </span>
            ))}
            {person.tasks.overdue > 0 && (
              <span
                className="rounded-full px-2 py-0.5 text-[10.5px] font-medium"
                style={{ background: 'var(--state-bad-bg)', color: 'var(--dot-flag)' }}
              >
                {person.tasks.overdue} overdue
              </span>
            )}
          </div>
        </div>

        <FlagCount n={person.flags.open} />
        <ChevronRight className="size-4 flex-none text-ink-ghost" />
      </Link>
    </li>
  );
}

function OversightPage() {
  const { user } = useAuth();
  const { request } = useAppRequest();

  const [people, setPeople] = useState<PersonRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState<Bucket>('all');
  const [query, setQuery] = useState('');

  const role = (user?.orgRole ?? 'EMPLOYEE') as OrgRole;

  const load = useCallback(async () => {
    // Two ranks, one list. Splitting them into tabs meant a Head had to look in
    // two places to answer "who has not clocked in", and each tab carried its
    // own count of the same day.
    const [employeeResult, managerResult] = await Promise.all([
      request<{ people: PersonRow[] }>('/api/oversight/people?role=EMPLOYEE'),
      request<{ people: PersonRow[] }>('/api/oversight/people?role=MANAGER'),
    ]);

    if (!employeeResult.ok) toast.error(employeeResult.error);

    const merged = [
      ...(employeeResult.ok ? employeeResult.data.people : []),
      ...(managerResult.ok ? managerResult.data.people : []),
    ].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

    setPeople(merged);
    setLoading(false);
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(
    () => ({
      all: people.length,
      in: people.filter((p) => inBucket(p, 'in')).length,
      leave: people.filter((p) => inBucket(p, 'leave')).length,
      out: people.filter((p) => inBucket(p, 'out')).length,
      flagged: people.filter((p) => inBucket(p, 'flagged')).length,
      overdue: people.filter((p) => inBucket(p, 'overdue')).length,
    }),
    [people],
  );

  const openFlags = useMemo(() => people.reduce((n, p) => n + p.flags.open, 0), [people]);

  const shown = useMemo(() => {
    const term = query.trim().toLowerCase();
    return people
      .filter((p) => inBucket(p, bucket))
      .filter(
        (p) =>
          !term ||
          (p.name ?? '').toLowerCase().includes(term) ||
          p.email.toLowerCase().includes(term),
      );
  }, [people, bucket, query]);

  // Manager and above. The API already scopes each row with canSeeRecordsOf,
  // so a Manager opening this sees only the Employees who report to them —
  // blocking the page outright left them nowhere to read their own team's flags.
  if (!isSupervisor(role)) return <Navigate to="/clock" replace />;

  const firstName = (user?.name ?? '').split(' ')[0] || 'there';

  return (
    <AppShell
      wide
      title="Company today"
      subtitle={
        openFlags === 0
          ? `${greeting()}, ${firstName}. Nothing is flagged right now.`
          : `${greeting()}, ${firstName}. ${openFlags} open flag${
              openFlags === 1 ? '' : 's'
            } across ${counts.flagged} ${counts.flagged === 1 ? 'person' : 'people'}.`
      }
    >
      {loading ? (
        <Skeleton className="h-96 w-full rounded-[18px]" />
      ) : (
        <div className="mx-auto flex max-w-3xl flex-col">
          {/* Every number here counts people, and every one of them opens. */}
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            <FilterTile
              n={counts.all}
              label="people"
              active={bucket === 'all'}
              onClick={() => setBucket('all')}
            />
            <FilterTile
              n={counts.in}
              label="clocked in"
              tone="good"
              active={bucket === 'in'}
              onClick={() => setBucket('in')}
            />
            <FilterTile
              n={counts.out}
              label="yet to clock in"
              tone="warn"
              active={bucket === 'out'}
              onClick={() => setBucket('out')}
            />
            <FilterTile
              n={counts.leave}
              label="on leave"
              active={bucket === 'leave'}
              onClick={() => setBucket('leave')}
            />
            <FilterTile
              n={counts.flagged}
              label="with flags"
              tone="alert"
              active={bucket === 'flagged'}
              onClick={() => setBucket('flagged')}
            />
            <FilterTile
              n={counts.overdue}
              label="with overdue"
              tone="alert"
              active={bucket === 'overdue'}
              onClick={() => setBucket('overdue')}
            />
          </div>

          <div className="mt-3 flex items-center gap-2 rounded-[12px] border border-rule bg-paper px-3 py-2">
            <Search className="size-4 flex-none text-ink-ghost" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email"
              className="min-w-0 flex-1 bg-transparent text-[13.5px] text-ink outline-none placeholder:text-ink-ghost"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="grid size-6 flex-none place-items-center rounded-full bg-paper-sunk text-ink-faint"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>

          {/* What the list is currently showing, said in words. */}
          <div className="mt-3 flex items-baseline justify-between">
            <span className="label-mono text-ink-faint">
              {bucket === 'all' ? 'Everyone' : 'Filtered'}
            </span>
            <span className="font-clock text-[12px] text-ink-faint">
              {shown.length} of {people.length}
              {bucket !== 'all' && (
                <button
                  type="button"
                  onClick={() => setBucket('all')}
                  className="ml-2 underline underline-offset-2 hover:text-ink"
                >
                  clear
                </button>
              )}
            </span>
          </div>

          {shown.length === 0 ? (
            <p className="mt-2 rounded-[14px] border border-dashed border-rule px-5 py-10 text-center text-[13px] text-ink-faint">
              {query ? `Nobody matches “${query}”.` : BUCKET_EMPTY[bucket]}
            </p>
          ) : (
            <ul>
              {shown.map((person) => (
                <PersonLine key={person.id} person={person} />
              ))}
            </ul>
          )}

          {/* The rule, stated once at the foot — flags are never typed by hand. */}
          <p
            className="mt-4 pt-3 text-[11.5px] leading-relaxed text-ink-ghost"
            style={{ borderTop: '1.5px solid var(--ink)' }}
          >
            Flags are raised automatically — late or missing clock-ins, off-site logins, and tasks
            past their deadline. Tap anyone to see the detail.
          </p>

          <Button variant="outline" className="mt-4 self-center" onClick={() => void load()}>
            Refresh
          </Button>
        </div>
      )}
    </AppShell>
  );
}

export default function OversightRoute() {
  return (
    <AuthGuard>
      <OversightPage />
    </AuthGuard>
  );
}
